/**
 * Notes viewing page - displays notes for one chapter at a time
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useBookNotes } from '../hooks/useBookNotes';
import { useToast } from '../contexts/ToastContext';
import NotesSidebar from '../components/notes/NotesSidebar';
import ChapterNotesView from '../components/notes/ChapterNotesView';
import ChatNavBar from '../components/chat/ChatNavBar';
import {
  ArrowLeftIcon,
  Bars3Icon,
  BookOpenIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  PlusIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { MessageAttachment } from '../types/chat';

export default function Notes() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeChapter, setActiveChapter] = useState<number>(1);
  const [isLoadingChapter, setIsLoadingChapter] = useState(false);
  const [highlightedNoteId, setHighlightedNoteId] = useState<string | null>(null);
  
  // Return to Dhyana state (from sessionStorage)
  const [returnToPage, setReturnToPage] = useState<{ bookId: string; pageNumber: number } | null>(null);

  // Track if initial chapter load has happened (to prevent re-running on polling)
  const initialLoadDoneRef = React.useRef(false);

  // Reset initial load ref when bookId changes
  React.useEffect(() => {
    initialLoadDoneRef.current = false;
  }, [bookId]);

  const {
    chaptersSummary,
    bookTitle,
    totalNotes,
    getChapterNotes,
    loading,
    loadingMore,
    error,
    searchQuery,
    setSearchQuery,
    selectedTypes,
    toggleTypeFilter,
    stats,
    fetchChapterNotes,
    loadMoreNotes,
    hasMoreNotes,
    createNote,
    updateNote,
    deleteNote,
    reindexNote,
    refetch
  } = useBookNotes(bookId || '');

  // Create note modal state
  const [showCreateNoteModal, setShowCreateNoteModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [noteAttachments, setNoteAttachments] = useState<MessageAttachment[]>([]);
  const imageInputRef = React.useRef<HTMLInputElement>(null);

  // Polling for indexing status
  const [pendingNoteIds, setPendingNoteIds] = useState<Set<string>>(new Set());
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 5000; // 5 seconds
  const MAX_POLLING_TIME = 120000; // 2 minutes max

  // Check for return page info from sessionStorage
  useEffect(() => {
    const returnData = sessionStorage.getItem('returnToPage');
    if (returnData) {
      try {
        const parsed = JSON.parse(returnData);
        if (parsed.bookId === bookId) {
          setReturnToPage(parsed);
        }
      } catch (e) {
        console.error('Failed to parse returnToPage data');
      }
    }
  }, [bookId]);

  // Handle return to Dhyana
  const handleReturnToDhyana = useCallback(() => {
    if (returnToPage) {
      sessionStorage.removeItem('returnToPage');
      navigate(`/dhyana/${returnToPage.bookId}?page=${returnToPage.pageNumber}`);
    }
  }, [returnToPage, navigate]);

  // Dismiss return button
  const dismissReturnButton = useCallback(() => {
    setReturnToPage(null);
    sessionStorage.removeItem('returnToPage');
  }, []);

  // Load Chapter 1 notes on mount, or the chapter containing the highlighted note
  // Only runs once when chaptersSummary first becomes available
  useEffect(() => {
    if (chaptersSummary.length > 0 && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;

      const highlightNote = searchParams.get('highlightNote');

      // Always fetch general notes (chapter 0) for the Book Notes section
      fetchChapterNotes(0);

      if (highlightNote) {
        // Find which chapter contains this note and load that chapter
        // For now, load chapter 1 and let the ChapterNotesView handle highlighting
        const firstChapter = chaptersSummary[0].chapter_number;
        setActiveChapter(firstChapter);
        fetchChapterNotes(firstChapter);
        setHighlightedNoteId(highlightNote);

        // Clear the query param after reading it
        setSearchParams({}, { replace: true });
      } else {
        const firstChapter = chaptersSummary[0].chapter_number;
        setActiveChapter(firstChapter);
        fetchChapterNotes(firstChapter);
      }
    }
  }, [chaptersSummary.length, searchParams, setSearchParams, fetchChapterNotes]);

  // Handle errors
  useEffect(() => {
    if (error) {
      showToast('error', 'Failed to load notes', error);
    }
  }, [error, showToast]);

  // Check if pending notes have been indexed (triggered by loadedChapters update)
  useEffect(() => {
    if (pendingNoteIds.size === 0) return;

    // Check the active chapter notes for indexing status updates
    const chapterNotes = getChapterNotes(activeChapter) || [];
    const stillPending = new Set<string>();

    pendingNoteIds.forEach(noteId => {
      const note = chapterNotes.find(n => n.id === noteId);
      if (note) {
        // Check if still pending
        if (!note.is_indexed && note.indexing_status !== 'completed' && note.indexing_status !== 'failed') {
          stillPending.add(noteId);
        }
      } else {
        // Note not in current chapter cache, keep polling
        stillPending.add(noteId);
      }
    });

    // Update pending set if changed
    if (stillPending.size !== pendingNoteIds.size) {
      setPendingNoteIds(stillPending);
    }
  }, [getChapterNotes, pendingNoteIds, activeChapter]);

  // Polling interval for indexing status updates
  useEffect(() => {
    if (pendingNoteIds.size === 0) {
      // No notes to poll for, clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Track poll start time for timeout
    const pollStartTime = Date.now();

    const pollForUpdates = async () => {
      // Check if we've exceeded max polling time
      if (Date.now() - pollStartTime > MAX_POLLING_TIME) {
        console.log('Indexing polling timeout reached, stopping polls');
        setPendingNoteIds(new Set());
        return;
      }

      // Refetch only the active chapter to get updated indexing status
      // The useEffect above will check if notes are indexed when loadedChapters updates
      await fetchChapterNotes(activeChapter, true, true); // forceRefetch=true, silent=true (no loading spinner)
    };

    // Set up interval for polling
    pollingIntervalRef.current = setInterval(pollForUpdates, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pendingNoteIds.size, fetchChapterNotes, activeChapter]);

  // Handle delete with toast
  const handleDelete = async (noteId: string) => {
    try {
      await deleteNote(noteId);
    } catch (err: any) {
      showToast('error', 'Failed to delete note', err.message);
      throw err;
    }
  };

  // Handle update with toast
  const handleUpdate = async (noteId: string, updates: any) => {
    try {
      const updatedNote = await updateNote(noteId, updates);

      // If the note is being re-indexed after edit, start polling
      if (updatedNote && !updatedNote.is_indexed) {
        setPendingNoteIds(prev => new Set(prev).add(noteId));
      }
    } catch (err: any) {
      showToast('error', 'Failed to update note', err.message);
      throw err;
    }
  };

  // Handle re-index with toast
  const handleReindex = async (noteId: string) => {
    try {
      await reindexNote(noteId);
    } catch (err: any) {
      showToast('error', 'Failed to re-index note', err.message);
      throw err;
    }
  };

  // Handle image selection for note attachments
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNoteAttachments(prev => [...prev, {
          type: 'image',
          data_url: reader.result as string,
          file_name: file.name,
          mime_type: file.type
        }]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input so same file can be selected again
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Remove image attachment
  const removeAttachment = (index: number) => {
    setNoteAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Handle create note
  const handleCreateNote = async () => {
    if (!newNoteText.trim() && noteAttachments.length === 0) return;

    setIsCreatingNote(true);
    try {
      const newNote = await createNote({
        page_number: 0, // General book note without specific page
        note_type: 'thought',
        user_note: newNoteText.trim(),
        image_urls: noteAttachments.map(att => att.data_url).filter(Boolean) as string[]
      });
      setNewNoteText('');
      setNoteAttachments([]);
      setShowCreateNoteModal(false);

      // Navigate to General Notes section to show the newly created note
      setActiveChapter(0);
      await fetchChapterNotes(0, true); // Force refresh to include new note
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Start polling for indexing status updates
      if (newNote?.id) {
        setPendingNoteIds(prev => new Set(prev).add(newNote.id));
      }
    } catch (err: any) {
      showToast('error', 'Failed to create note', err.message);
    } finally {
      setIsCreatingNote(false);
    }
  };

  // Close create note modal and reset state
  const closeCreateNoteModal = () => {
    setShowCreateNoteModal(false);
    setNewNoteText('');
    setNoteAttachments([]);
  };

  // Handle chapter selection
  const handleChapterSelect = async (chapterNumber: number) => {
    if (chapterNumber === activeChapter) return; // Already showing this chapter

    setActiveChapter(chapterNumber);
    setIsLoadingChapter(true);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Fetch notes if not already loaded
    await fetchChapterNotes(chapterNumber);
    setIsLoadingChapter(false);
  };

  // Prepare chapter info for sidebar
  // Filter out chapter 0 (general notes) since we show it separately above chapters
  const chapterInfo = chaptersSummary
    .filter((chapter) => chapter.chapter_number !== 0)
    .map((chapter) => ({
      number: chapter.chapter_number,
      name: chapter.chapter_name,
      noteCount: chapter.note_count
    }));

  if (loading && chaptersSummary.length === 0) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#030712] flex flex-col">
        <ChatNavBar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
              style={{ borderColor: 'rgba(6, 182, 212, 0.3)', borderTopColor: 'transparent' }}
            ></div>
            <p className="mt-4 text-sm text-gray-600 dark:text-slate-400">Loading your notes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#030712] flex flex-col">
      {/* Top Navigation Bar */}
      <ChatNavBar />

      {/* Header */}
      <header className="sticky top-[52px] z-40 lg:ml-64 bg-white dark:bg-[#030712] border-b border-gray-200 dark:border-slate-700/50">
        <div className="px-4 md:px-6 lg:px-8 py-3 md:py-6">
          {/* Mobile Layout - Simplified single row */}
          <div className="flex items-center gap-3 md:hidden">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors flex-shrink-0"
            >
              <Bars3Icon className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            </button>

            {/* Back Button */}
            <button
              onClick={() => navigate('/library')}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors flex-shrink-0"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            </button>

            {/* Title & Stats - Centered, takes remaining space */}
            <div className="flex-1 text-center min-w-0 pr-8">
              <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {bookTitle || 'Loading...'}
              </h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">
                {totalNotes} {totalNotes === 1 ? 'note' : 'notes'} · {chaptersSummary.length} chapters
              </p>
            </div>
          </div>

          {/* Desktop/Tablet Layout */}
          <div className="hidden md:flex items-center justify-between gap-8">
            {/* Left side - Mobile menu and back button */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors"
              >
                <Bars3Icon className="w-5 h-5 text-gray-600 dark:text-slate-400" />
              </button>

              {/* Back Button (tablet only - desktop has it in sidebar) */}
              <button
                onClick={() => navigate('/library')}
                className="lg:hidden flex items-center gap-1.5 text-sm text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-cyan-400 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Library</span>
              </button>
            </div>

            {/* Center - Title & Stats */}
            <div className="flex-1 text-center min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                {bookTitle || 'Loading...'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5 font-mono">
                {totalNotes} {totalNotes === 1 ? 'note' : 'notes'} · {chaptersSummary.length} {chaptersSummary.length === 1 ? 'chapter' : 'chapters'}
              </p>
            </div>

            {/* Right side - Search Bar */}
            <div className="w-72 flex-shrink-0">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notes..."
                  className="w-full pl-10 pr-12 py-2 border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800/50 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors text-sm"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-slate-500 font-mono">⌘K</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <NotesSidebar
          selectedTypes={selectedTypes}
          onToggleType={toggleTypeFilter}
          chapters={chapterInfo}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeChapter={activeChapter}
          onChapterSelect={handleChapterSelect}
          onBackClick={() => navigate('/library')}
          generalNotesCount={(getChapterNotes(0) || []).length}
        />

        {/* Main Content */}
        <main
          className="flex-1 lg:ml-64 min-h-[calc(100vh-120px)] md:min-h-0 bg-[radial-gradient(circle,rgba(229,231,235,0.4)_1.2px,transparent_1.2px)] dark:bg-[radial-gradient(circle,rgba(51,65,85,0.2)_1.2px,transparent_1.2px)] [background-size:24px_24px]"
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-12">
            {/* Empty State */}
            {chaptersSummary.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-800/50 mb-4">
                  <BookOpenIcon className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No notes yet
                </h2>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  Start reading and taking notes to see them organized here by chapters.
                </p>
                <button
                  onClick={() => navigate(`/dhyana/${bookId}`)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-white rounded-xl font-medium transition-all"
                  style={{ 
                    background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                    boxShadow: '0 0 20px rgba(6, 182, 212, 0.25)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(6, 182, 212, 0.25)';
                  }}
                >
                  <BookOpenIcon className="w-4 h-4" />
                  Start Reading
                </button>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <ExclamationCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
                      Failed to load notes
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    <button
                      onClick={() => refetch()}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-semibold"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Chapter/General Notes View */}
            {(chaptersSummary.length > 0 || activeChapter === 0) && (
              <ChapterNotesView
                chapterNumber={activeChapter}
                chapterName={activeChapter === 0 ? 'General Notes' : (chaptersSummary.find(ch => ch.chapter_number === activeChapter)?.chapter_name || '')}
                notes={getChapterNotes(activeChapter)}
                bookId={bookId || ''}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                onReindex={handleReindex}
                onLoadMore={() => loadMoreNotes(activeChapter)}
                hasMore={hasMoreNotes(activeChapter)}
                isLoadingMore={loadingMore}
                searchQuery={searchQuery}
                selectedTypes={selectedTypes}
                isLoading={isLoadingChapter}
                highlightedNoteId={highlightedNoteId}
              />
            )}
          </div>
        </main>
      </div>

      {/* Floating Return to Dhyana Button */}
      {returnToPage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-slide-up">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg backdrop-blur-md bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50">
            <button
              onClick={handleReturnToDhyana}
              className="flex items-center gap-2 font-medium text-slate-800 dark:text-white hover:opacity-80 transition-opacity"
            >
              <ArrowLeftIcon className="h-4 w-4 text-cyan-500" />
              <span>Return to Page {returnToPage.pageNumber}</span>
            </button>
            <button
              onClick={dismissReturnButton}
              className="p-1 rounded-full hover:bg-slate-500/20 transition-colors text-slate-400 dark:text-slate-500"
              title="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Floating Action Button - Create Note */}
      <button
        onClick={() => setShowCreateNoteModal(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
        style={{
          background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
          boxShadow: '0 4px 20px rgba(6, 182, 212, 0.35)'
        }}
        title="Create new note"
      >
        <PlusIcon className="w-6 h-6 text-white" />
      </button>

      {/* Create Note Modal */}
      {showCreateNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeCreateNoteModal}
          />

          {/* Modal */}
          <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create Note
              </h2>
              <button
                onClick={closeCreateNoteModal}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Write your thoughts..."
                className="w-full h-32 px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors resize-none"
                autoFocus
              />

              {/* Image Attachments Preview */}
              {noteAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {noteAttachments.map((attachment, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={attachment.data_url}
                        alt={attachment.file_name || `Image ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-slate-700"
                      />
                      <button
                        onClick={() => removeAttachment(index)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Image Button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-cyan-500 dark:hover:text-cyan-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                  <span>Add Image</span>
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              <p className="text-xs text-gray-500 dark:text-slate-400">
                This note will be added as a general thought for the book.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-slate-700">
              <button
                onClick={closeCreateNoteModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNote}
                disabled={(!newNoteText.trim() && noteAttachments.length === 0) || isCreatingNote}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)'
                }}
              >
                {isCreatingNote ? 'Creating...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
